import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useLocation } from 'react-router-dom'
import {
  EDITION_PROFILE,
  editionFromSearch,
  getEdition,
  isEditionLocked,
  setActiveEdition,
  type Edition,
  type EditionProfile,
} from '../lib/edition'
import { AppThemeProvider, GlobalStyles } from '../styles/theme'
import { editionThemeOverride } from '../styles/editionTheme'

interface EditionContextValue {
  edition: Edition
  profile: EditionProfile
  /** Imperatively switch edition (also persists + updates the API scope). */
  setEdition: (edition: Edition) => void
  /**
   * True when the mount prefix owns the edition.
   *
   * A locked mount ignores `?edition=` and the switcher, so the UI must not
   * offer them: an affordance that silently does nothing is worse than none.
   */
  locked: boolean
}

const EditionContext = createContext<EditionContextValue | null>(null)

/**
 * Resolves the active edition for this mount and keeps the module-level cache
 * (read by the API client) in sync, so edition scope rides with auth on every
 * request. Lives inside the router.
 *
 * The mount prefix wins over the URL: `/chowdash` is ChowDash regardless of
 * `?edition=`, and on that mount `setEdition` is inert (see lib/edition.ts).
 */
export const EditionProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation()
  const locked = isEditionLocked()
  // Read the URL for the first render too, not just in the effect below.
  //
  // The effect version is one render late, and that render is not cosmetic: App
  // picks the product from the edition, so the first paint mounts the customer
  // app and its auth guard runs before the rider app is ever considered. On any
  // guarded route that bounce lands the rider on /login. The root mount is only
  // safe from it by luck, because its landing route has no guard.
  const [edition, setEditionState] = useState<Edition>(
    () => (locked ? getEdition() : editionFromSearch(location.search) ?? getEdition()),
  )

  useEffect(() => {
    if (locked) return
    const fromUrl = editionFromSearch(location.search)
    if (fromUrl && fromUrl !== edition) {
      setActiveEdition(fromUrl)
      setEditionState(fromUrl)
    }
  }, [location.search, edition, locked])

  const setEdition = useCallback(
    (next: Edition) => {
      setActiveEdition(next)
      setEditionState(next)
    },
    [],
  )

  const value = useMemo<EditionContextValue>(
    () => ({ edition, profile: EDITION_PROFILE[edition], setEdition, locked }),
    [edition, setEdition, locked],
  )

  return (
    <EditionContext.Provider value={value}>
      <AppThemeProvider theme={editionThemeOverride(edition)}>
        <GlobalStyles />
        {children}
      </AppThemeProvider>
    </EditionContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const useEdition = (): EditionContextValue => {
  const ctx = useContext(EditionContext)
  if (!ctx) {
    throw new Error('useEdition must be used within an EditionProvider')
  }
  return ctx
}
